import React from 'react'
import useInsightsSection from './hooks/useInsightsSection'
import s from './styles/InsightsSection.module.scss'
import { ReactComponent as BoltIcon } from 'assets/icons/bolt.svg'
import { ReactComponent as EyeIcon } from 'assets/icons/eye-close.svg'
import { ReactComponent as UniversityIcon } from 'assets/icons/university.svg'
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg'
import { ReactComponent as WarningIcon } from 'assets/icons/warning.svg'
import LoaderImg from 'shared/components/LoaderImg/LoaderImg'

const InsightsSection = ({
	superpowers = [],
	blindspots = [],
	campusVibe = null,
	hasTraitScores = true,
	isLoading = false,
}) => {
	const vm = useInsightsSection({ superpowers, blindspots, campusVibe })
	const emptyHint = hasTraitScores
		? 'Retake the quiz to surface strengths.'
		: 'This student has no saved trait scores.'
	const blindspotHint = hasTraitScores
		? 'No obvious growth areas now.'
		: 'This student has no saved trait scores.'

	return (
		<section className={s.grid} aria-label="Personal insights">
			<article
				className={[s.card, isLoading && s.loading].filter(Boolean).join(' ')}
			>
				<div className={s.head}>
					<span className={`${s.head__icon} ${s.head__iconGreen}`} aria-hidden>
						<BoltIcon />
					</span>
					<h3 className={s.head__title}>Superpowers</h3>
				</div>
				<ul className={s.list}>
					{isLoading || vm?.superpowers?.length ? (
						(isLoading
							? [...Array(2)].map((_, i) => i + 1)
							: vm.superpowers
						).map(sp => (
							<li key={sp.dim} className={s.item}>
								<span
									className={`${s.item__icon} ${s.item__iconGreen}`}
									aria-hidden
								>
									<CheckIcon />
								</span>
								<div className={s.item__body}>
									<p className={s.item__label}>
										{isLoading ? <LoaderImg /> : sp.dimLabel}
									</p>
									<p className={s.item__text}>
										{isLoading ? <LoaderImg /> : sp.copy}
									</p>
								</div>
							</li>
						))
					) : (
						<li className={s.empty}>{emptyHint}</li>
					)}
				</ul>
			</article>

			<article
				className={[s.card, isLoading && s.loading].filter(Boolean).join(' ')}
			>
				<div className={s.head}>
					<span className={`${s.head__icon} ${s.head__iconRed}`} aria-hidden>
						<EyeIcon />
					</span>
					<h3 className={s.head__title}>Potential Blind Spots</h3>
				</div>
				<ul className={s.list}>
					{isLoading || vm.blindspots.length ? (
						(isLoading
							? [...Array(2)].map((_, i) => i + 1)
							: vm.blindspots
						).map(bs => (
							<li key={bs.dim} className={s.item}>
								<span
									className={`${s.item__icon} ${s.item__iconRed}`}
									aria-hidden
								>
									<WarningIcon />
								</span>
								<div className={s.item__body}>
									<p className={s.item__label}>
										{isLoading ? <LoaderImg /> : bs.dimLabel}
									</p>
									<p className={s.item__text}>
										{isLoading ? <LoaderImg /> : bs.copy}
									</p>
								</div>
							</li>
						))
					) : (
						<li className={s.empty}>{blindspotHint}</li>
					)}
				</ul>
			</article>

			<article className={s.card}>
				<div className={s.head}>
					<span className={`${s.head__icon} ${s.head__iconPurple}`} aria-hidden>
						<UniversityIcon />
					</span>
					<h3 className={s.head__title}>Ideal Campus Vibe</h3>
				</div>
				<p className={s.text}>
					{isLoading ? <LoaderImg /> : vm.campusVibe?.blurb}
				</p>
				<div className={s.tags}>
					{isLoading
						? [...Array(3)].map((_, i) => (
								<span
									key={i}
									className={[s.tag, s.tagLoading].filter(Boolean).join(' ')}
								>
									<LoaderImg />
								</span>
							))
						: (vm.campusVibe?.chips || []).map(chip => (
								<span key={chip} className={s.tag}>
									{chip}
								</span>
							))}
				</div>
			</article>
		</section>
	)
}

export default InsightsSection
